"use client"

import { ChevronRight } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { useAuth } from "@/auth"

import type { NavItem } from "@/config"
import type { NavSingleItem } from "./nav-single"
import { QuickAccessButton } from "@/components/layout/quick-access-button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/shared/components/ui/sidebar'

// Helper para obtener el nombre del icono como string
function getIconName(icon: React.ComponentType): string {
  return (icon as { displayName?: string }).displayName || icon.name || "Circle"
}

export function NavMain({
  items,
  singleItems,
}: {
  items: NavItem[]
  singleItems?: NavSingleItem[]
}) {
  const location = useLocation()
  const { state, setOpen } = useSidebar()
  const { user } = useAuth()
  const userGroups = user?.groups || []
  const isSuperuser = user?.is_superuser

  const hasAccess = (requiredGroups?: string[]) => {
    if (!requiredGroups) return true
    if (isSuperuser) return true
    return requiredGroups.some(g => userGroups.includes(g))
  }

  const handleMenuClick = () => {
    if (state === "collapsed") {
      setOpen(true)
    }
  }

  // Check if any subitem matches the current path
  const isItemActive = (item: NavItem) => {
    if (!item.items) return false
    return item.items.some(
      (subItem) =>
        location.pathname === subItem.url ||
        location.pathname.startsWith(subItem.url + "/")
    )
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Plataforma</SidebarGroupLabel>
      <SidebarMenu>
        {/* Items unicos (sin subitems) */}
        {singleItems?.map((item) => {
          const isActive = location.pathname === item.url
          const itemId = `single-${item.title.toLowerCase().replace(/\s+/g, "-")}`

          return (
            <SidebarMenuItem key={item.title} className="group/menu-item relative">
              <SidebarMenuButton asChild tooltip={item.title} isActive={isActive}>
                <Link to={item.url}>
                  <item.icon />
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
              <QuickAccessButton
                item={{
                  id: itemId,
                  title: item.title,
                  url: item.url,
                  icon: getIconName(item.icon),
                }}
              />
            </SidebarMenuItem>
          )
        })}

        {/* Items con subitems (collapsibles) */}
        {items.filter((item) => hasAccess(item.groups)).map((item) => {
          const isActive = isItemActive(item)

          return (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={isActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={item.title} onClick={handleMenuClick} isActive={isActive}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.filter((subItem) => hasAccess(subItem.groups)).map((subItem) => {
                      const isExternalUrl = subItem.url.startsWith('http')
                      const isSubActive = !isExternalUrl && location.pathname === subItem.url
                      const subItemId = `${item.title.toLowerCase().replace(/\s+/g, "-")}-${subItem.title.toLowerCase().replace(/\s+/g, "-")}`

                      return (
                        <SidebarMenuSubItem key={subItem.title} className="group/sub-item relative">
                          <SidebarMenuSubButton asChild isActive={isSubActive}>
                            {isExternalUrl ? (
                              <a href={subItem.url} {...(subItem.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                <span>{subItem.title}</span>
                              </a>
                            ) : (
                              <Link to={subItem.url}>
                                <span>{subItem.title}</span>
                              </Link>
                            )}
                          </SidebarMenuSubButton>
                          <QuickAccessButton
                            variant="subitem"
                            item={{
                              id: subItemId,
                              title: subItem.title,
                              url: subItem.url,
                              icon: item.icon ? getIconName(item.icon) : "Circle",
                            }}
                          />
                        </SidebarMenuSubItem>
                      )
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
